import { ReactNode } from 'react';
import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
} from 'react-aria-components';
import { cx } from './cx';

export type IconButtonVariant = 'ghost' | 'secondary';
export type IconButtonSize = 'xs' | 'sm' | 'md';

const base =
  'appearance-none bg-transparent inline-flex items-center justify-center rounded-md outline-none transition cursor-pointer ' +
  'focus-visible:ring-2 focus-visible:ring-[rgb(var(--c-ring)/0.5)] disabled:cursor-not-allowed disabled:opacity-60';

const sizes: Record<IconButtonSize, string> = {
  xs: 'size-5',
  sm: 'size-8 rounded-lg',
  md: 'size-9 rounded-lg',
};

const variants: Record<IconButtonVariant, string> = {
  ghost: 'text-fg-tertiary hover:bg-neutral hover:text-fg-secondary',
  secondary:
    'border border-strong bg-surface text-fg-secondary shadow-xs hover:bg-muted-strong',
};

export interface IconButtonProps extends Omit<
  AriaButtonProps,
  'children' | 'className'
> {
  /** Required — icon-only buttons must name their action for screen readers. */
  'aria-label': string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  className?: string;
  children: ReactNode;
}

/**
 * Icon-only button (react-aria `Button`) for table row actions, toolbars and
 * compact controls. `ghost` is the quiet row-action style; `secondary` adds a
 * border + surface. Always pass `aria-label`.
 */
export const IconButton = ({
  variant = 'ghost',
  size = 'md',
  className,
  children,
  ...props
}: IconButtonProps) => (
  <AriaButton
    {...props}
    className={cx(base, sizes[size], variants[variant], className)}
  >
    {children}
  </AriaButton>
);
